import { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useLocation } from "react-router-dom";
import { FiArrowRight, FiChevronDown, FiClock, FiFileText, FiMessageSquare, FiMicOff, FiPause, FiPhone, FiPhoneIncoming, FiPhoneOutgoing, FiPlay, FiSearch, FiVolume2, FiX } from "react-icons/fi";
import { MdCancel, MdOutlineAnalytics } from "react-icons/md";
import { BsPerson, BsRobot } from "react-icons/bs";
import { RiCustomerService2Fill } from "react-icons/ri";
import { S3Client, GetObjectCommand } from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import RequestSentDetailsDS from "../../DataServices/RequestSentDetailsDS";
import { useCustomContext } from "../CustomComponents/CustomComponents";
import CallRecordingPlayerModal from "../RequestSentDetails/RecordPlayerModal";
import { insertLog } from "../Logs/InsertLog";
import "../RequestSentDetails/AgenctTicketHistory/ticketDetails.css";
import "./Call_History.css";
import { BsTelephoneInbound } from "react-icons/bs";
import { BsTelephoneOutbound } from "react-icons/bs";

const requestSentDS = new RequestSentDetailsDS();

const parseIfJsonString = (value) => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const getAnalysisData = (item) => {
  const parsed = parseIfJsonString(item?.call_analysis_data);
  return parsed && typeof parsed === "object" ? parsed : null;
};

const formatDuration = (seconds) => {
  const total = Math.floor(Number(seconds) || 0);
  const mins = String(Math.floor(total / 60)).padStart(2, "0");
  const secs = String(total % 60).padStart(2, "0");
  return `${mins}:${secs}`;
};

const getStatusStyle = (status) => {
  if (status === "Unable to contact") {
    return { color: "#d32f2f", background: "#fdecea", border: "1px solid #ef9a9a" };
  }
  if (status === "Left voice message") {
    return { color: "#1976d2", background: "#e3f2fd", border: "1px solid #90caf9" };
  }
  if (status === "Spoke to person") {
    return { color: "#2e7d32", background: "#e8f5e9", border: "1px solid #81c784" };
  }
  return { color: "#555", background: "#f5f5f5", border: "1px solid #d1d5db" };
};

const isInterruptedPlayError = (err) =>
  err?.name === "AbortError" ||
  err?.message?.includes("play() request was interrupted");

const CallHistory = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const { hud, stopHudRotation, showAlert } = useCustomContext();

  const initialVinId = queryParams.get("requestID") || queryParams.get("vin_id") || "";

  const [vinId, setVinId] = useState(initialVinId);
  const [callHistory, setCallHistory] = useState([]);
  const [expandedAnalysis, setExpandedAnalysis] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalItem, setModalItem] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [incoming, setIncoming] = useState(true);
  const audioRef = useRef(null);
  const navigationLogRef = useRef(false);
  const useremail = localStorage.getItem("email");
  const userName = useremail?.split("@")[0] || "";

  useEffect(() => {
    if (navigationLogRef.current) return;
    navigationLogRef.current = true;

    insertLog(
      `${userName} navigated to Call History screen successfully`,
      0,
      useremail,
      hud,
      stopHudRotation,
      showAlert,
      '',
      'GET',
      'Call_History',
      'SUCCESS',
      ''
    );
  }, [hud, showAlert, stopHudRotation, useremail]);

  useEffect(() => {
    fetchCallHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incoming]);

  const filteredCallHistory = useMemo(() => {
    const searchText = String(vinId || "").trim().toLowerCase();
    if (!searchText) return callHistory;

    return callHistory.filter((item) => {
      const itemVin = item?.vin_id || item?.vinID || item?.vin || item?.requestID || item?.request_id || "";
      return String(itemVin).toLowerCase().includes(searchText);
    });
  }, [callHistory, vinId]);

  const getAudioUrl = async (key) => {
    const idToken = localStorage.getItem("idToken");
    const region = process.env.REACT_APP_AWS_REGION;
    const s3 = new S3Client({
      region,
      credentials: fromCognitoIdentityPool({
        clientConfig: { region },
        identityPoolId: process.env.REACT_APP_CONNECT_IDENTITY_POOL_ID,
        logins: {
          [`cognito-idp.${region}.amazonaws.com/${process.env.REACT_APP_COGNITO_USERPOOLID}`]: idToken,
        },
      }),
    });

    const command = new GetObjectCommand({
      Bucket: process.env.REACT_APP_CONNECT_BUCKET,
      Key: key,
    });

    return getSignedUrl(s3, command, { expiresIn: 3600 });
  };

  const fetchCallHistory = async () => {
    setLoading(true);
    hud("Please Wait..");
    try {
      const response = await requestSentDS.AC_Incoming_Call_history_Get({ isIncoming: incoming });
      const data = response.data;
      const body = Array.isArray(data.body)
        ? data.body
        : typeof data.body === "string"
          ? JSON.parse(data.body)
          : data.body;
      const rows = Array.isArray(body)
        ? body
        : Array.isArray(body?.data)
          ? body.data
          : Array.isArray(body?.callHistory)
            ? body.callHistory
            : [];

      if (data.statusCode === 200) {
        setCallHistory(rows);
        insertLog(
          `${userName} fetched ${incoming ? "incoming" : "outgoing"} call history successfully`,
          0,
          useremail,
          hud,
          stopHudRotation,
          showAlert,
          '',
          'GET',
          'AC_Incoming_Call_history_Get',
          'SUCCESS',
          ''
        );
      } else {
        setCallHistory([]);
        insertLog(
          `${userName} failed to fetch ${incoming ? "incoming" : "outgoing"} call history`,
          0,
          useremail,
          hud,
          stopHudRotation,
          showAlert,
          '',
          'GET',
          'AC_Incoming_Call_history_Get',
          'FAILED',
          body?.message || "Failed to load call history."
        );
        showAlert(body?.message || "Failed to load call history.", [
          { label: "OK", onClick: () => { }, color: "var(--buttonColor)" }
        ]);
      }
    } catch (err) {
      setCallHistory([]);
      insertLog(
        `${userName} failed to fetch ${incoming ? "incoming" : "outgoing"} call history`,
        0,
        useremail,
        hud,
        stopHudRotation,
        showAlert,
        '',
        'GET',
        'AC_Incoming_Call_history_Get',
        'FAILED',
        err.message
      );
      showAlert(err.message || "Something went wrong while fetching call history.", [
        { label: "OK", onClick: () => { }, color: "var(--buttonColor)" }
      ]);
    } finally {
      setLoading(false);
      stopHudRotation();
    }
  };

  const handlePlay = async (key, id) => {
    try {
      if (playingId === id && audioRef.current) {
        if (isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
        } else {
          try {
            await audioRef.current.play();
            setIsPlaying(true);
          } catch (err) {
            if (!isInterruptedPlayError(err)) {
              throw err;
            }
            setIsPlaying(false);
          }
        }
        return;
      }

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      const url = await getAudioUrl(key);
      setAudioUrl(url);
      setPlayingId(id);

      if (audioRef.current) {
        audioRef.current.src = url;
        try {
          await audioRef.current.play();
          setIsPlaying(true);
        } catch (err) {
          if (!isInterruptedPlayError(err)) {
            throw err;
          }
          setIsPlaying(false);
        }
      }
    } catch (err) {
      console.error("Error playing audio:", err);
    }
  };

  const closeAnalysisModal = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = "";
    }
    setIsPlaying(false);
    setPlayingId(null);
    setAudioUrl(null);
    setExpandedAnalysis(null);
  };

  return (
    <div className="call-history-page">

      <div className="custom1-header">
        <span className="title1-req">Call History{incoming ? " - Incoming " : " - Outgoing "}</span>

        <div className="call-history-direction-switch" role="group" aria-label="Call direction">
          <button
            className={`call-history-direction-option ${incoming ? "active inbound" : ""}`}
            type="button"
            onClick={() => setIncoming(true)}
            title="Inbound Calls"
          >
            <BsTelephoneInbound />
            <span>Incoming</span>
          </button>
          <button
            className={`call-history-direction-option ${!incoming ? "active outbound" : ""}`}
            type="button"
            onClick={() => setIncoming(false)}
            title="Outbound Calls"
          >
            <BsTelephoneOutbound />
            <span>Outgoing</span>
          </button>
        </div>
      </div>

      <div className="call-history-body">
        {(!filteredCallHistory || filteredCallHistory.length === 0) ? (
          <div className="empty-text_ticket">
            <p>No call history found.</p>
          </div>
        ) : (
          <div className="callHistory-table-content">
            <div className="call-history-cards">
              {filteredCallHistory.map((item, index) => {
                const analysisData = getAnalysisData(item);
                const duration = formatDuration(item.call_duration);
                const date = item.updated_at ? new Date(item.updated_at).toLocaleString() : "-";

                return (
                  <div className="call-history-card" key={item._id || index}>
                    <audio
                      ref={audioRef}
                      onEnded={() => {
                        setIsPlaying(false);
                        setPlayingId(null);
                      }}
                    />

                    <div className="call-history-card-strip" />
                    <div className="call-history-card-header">
                      <div className="call-history-agent">
                        {item.call_s3_key && analysisData && (
                          <div className="call-history-recording-row">
                            <button
                              className="call-history-play-btn"
                              type="button"
                              onClick={() => {
                                handlePlay(item.call_s3_key, item._id);
                                setModalItem({ ...item, call_analysis_data: analysisData });
                                setModalOpen(true);
                              }}
                            >
                              {playingId === item._id && isPlaying ? <FiPause size={14} /> : <FiPlay size={14} />}
                            </button>
                          </div>
                        )}
                        <div>
                          <div className="call-history-agent-email">{item.agent_email || "-"}</div>
                          <div className="call-history-date">
                            <FiClock size={12} /> {date}
                          </div>
                        </div>
                      </div>

                      {!analysisData && (
                        <div className="call-history-recording-row">
                          <span style={{ fontSize: "12px" }}>No Recording</span>
                        </div>
                      )}

                      {analysisData && item.call_duration != null && (
                        <div className="call-history-duration-badge">
                          <FiClock size={12} /> {duration}
                        </div>
                      )}
                    </div>

                    <div className="call-history-card-body">
                      <div className="call-history-info-row">
                        <div className="call-history-info-box">
                          <span className="call-history-info-icon">
                            <FiPhoneOutgoing style={{ color: "#1976d2" }} />
                          </span>
                          <div>
                            <span className="call-history-label">From</span>
                            <span className="call-history-value">{item.called_from || "-"}</span>
                          </div>
                        </div>
                        <div className="call-history-info-divider" />
                        <div className="call-history-info-box">
                          <span className="call-history-info-icon">
                            <FiPhone style={{ color: "#2e7d32" }} />
                          </span>
                          <div>
                            <span className="call-history-label">To</span>
                            <span className="call-history-value">{item.called_to || "-"}</span>
                          </div>
                        </div>
                      </div>

                      <div className="call-history-notes-box">
                        <span className="call-history-label">Notes</span>
                        <p className="call-history-notes-text">{item.notes || "No notes added."}</p>
                      </div>

                      <div className={`ca-trigger-row ${incoming ? "incoming" : ""}`}>
                        {item.call_status && (
                          <span className="call-history-status" style={getStatusStyle(item.call_status)}>
                            {item.call_status}
                          </span>
                        )}

                        {analysisData && (
                          <button
                            className="ca-trigger-btn"
                            type="button"
                            onClick={() => setExpandedAnalysis((prev) => prev === item._id ? null : item._id)}
                          >
                            <MdOutlineAnalytics size={11} />
                            <span>Analysis</span>
                            <FiChevronDown
                              size={10}
                              style={{
                                transition: "transform 0.2s",
                                transform: expandedAnalysis === item._id ? "rotate(180deg)" : "rotate(0deg)",
                              }}
                            />
                          </button>
                        )}
                      </div>

                      {analysisData && expandedAnalysis === item._id && ReactDOM.createPortal(
                        <>
                          <div className="ca-backdrop" />
                          <div className="ca-modal">
                            <div className="ca-modal-header">
                              <div className="ca-modal-title">
                                <MdOutlineAnalytics size={15} />
                                <span>Call Details</span>
                              </div>
                              <button className="ca-modal-close" type="button" onClick={closeAnalysisModal}>
                                <MdCancel size={13} />
                              </button>
                            </div>

                            <div className="ca-modal-body">
                              <div className="ca-col ca-col-left">
                                <div className="ca-section-label">
                                  <FiVolume2 size={10} /> Audio Recording
                                </div>
                                <div className="ca-info-header">
                                  {item.call_s3_key ? (
                                    <button
                                      className="ca-play-btn"
                                      type="button"
                                      onClick={() => {
                                        handlePlay(item.call_s3_key, item._id);
                                        setModalItem({ ...item, call_analysis_data: analysisData });
                                        setModalOpen(true);
                                      }}
                                    >
                                      {playingId === item._id && isPlaying ? <FiPause size={15} /> : <FiPlay size={15} />}
                                    </button>
                                  ) : (
                                    <div className="ca-no-avatar">
                                      <FiMicOff size={15} /> No recording available
                                    </div>
                                  )}
                                  <div className="ca-agent-info">
                                    <span className="ca-agent-name">{item.agent_email || "-"}</span>
                                    <span className="ca-agent-date">
                                      <FiClock size={10} />
                                      {date}
                                    </span>
                                  </div>

                                  {item.call_s3_key && (
                                    <div className="ca-duration-pill">
                                      <FiClock size={10} />
                                      <span>{duration}</span>
                                    </div>
                                  )}
                                </div>

                                <div className="ca-section-label">
                                  <FiPhone size={10} /> Call Route
                                </div>
                                <div className="ca-from-to">
                                  <div className="ca-route-box from">
                                    <span className="ca-route-label">
                                      <FiPhoneOutgoing size={10} /> From
                                    </span>
                                    <span className="ca-route-value">{item.called_from || "-"}</span>
                                  </div>
                                  <div className="ca-route-arrow">
                                    <FiArrowRight size={14} />
                                  </div>
                                  <div className="ca-route-box to">
                                    <span className="ca-route-label">
                                      <FiPhoneIncoming size={10} /> To
                                    </span>
                                    <span className="ca-route-value">{item.called_to || "-"}</span>
                                  </div>
                                </div>

                                <div className="ca-section-label">
                                  <FiFileText size={10} /> Notes
                                </div>
                                <p className="ca-notes-text">{item.notes || "No notes added."}</p>

                                {analysisData.call_issues && (
                                  <>
                                    <div className="ca-section-label issues-label">
                                      <FiFileText size={10} /> Issues
                                    </div>
                                    <p className="ca-block-text issues">{analysisData.call_issues}</p>
                                  </>
                                )}
                              </div>

                              <div className="ca-col ca-col-right">
                                <div className="ca-section-label">
                                  <BsRobot size={10} /> Call Analysis
                                </div>
                                <div className="ca-sentiment-row">
                                  <div className="ca-sentiment agent">
                                    <RiCustomerService2Fill size={15} />
                                    <div>
                                      <span className="ca-sent-label">Agent</span>
                                      <span className="ca-sent-value">{analysisData.agent_sentiment || "-"}</span>
                                    </div>
                                  </div>
                                  <div className="ca-sentiment customer">
                                    <BsPerson size={15} />
                                    <div>
                                      <span className="ca-sent-label">Customer</span>
                                      <span className="ca-sent-value">{analysisData.customer_sentiment || "-"}</span>
                                    </div>
                                  </div>
                                </div>

                                {analysisData.call_summary && (
                                  <>
                                    <div className="ca-section-label">
                                      <FiFileText size={10} /> Summary
                                    </div>
                                    <p className="ca-block-text">{analysisData.call_summary}</p>
                                  </>
                                )}

                                {analysisData.call_transcript?.length > 0 && (
                                  <>
                                    <div className="ca-section-label">
                                      <FiMessageSquare size={10} /> Transcript
                                    </div>
                                    <div className="ca-transcript-list">
                                      {analysisData.call_transcript.map((line, lineIndex) => {
                                        const isCustomer = line.startsWith("Customer:");
                                        const text = line.replace(/^(Customer|Agent):\s*/, "");
                                        return (
                                          <div key={lineIndex} className={`ca-bubble-row ${isCustomer ? "agent" : "customer"}`}>
                                            <div className="ca-bubble">
                                              <span className="ca-speaker">
                                                {isCustomer ? <><BsPerson size={9} /> Customer</> : <><RiCustomerService2Fill size={9} /> Agent</>}
                                              </span>
                                              <p className="ca-bubble-text">{text}</p>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </>,
                        document.body
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <CallRecordingPlayerModal
        isOpen={modalOpen}
        onClose={() => {
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            audioRef.current.src = "";
          }
          setIsPlaying(false);
          setPlayingId(null);
          setAudioUrl(null);
          setModalOpen(false);
        }}
        audioRef={audioRef}
        audioUrl={audioUrl}
        callItem={modalItem}
      />
    </div>
  );
};

export default CallHistory;
